import React, { useState } from "react";
import { Head, useForm, router } from "@inertiajs/react";
import { formatDateEST } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SeedEmail {
  id: number;
  email: string;
  status: string;
  access_token?: string;
  token_expires_at?: string;
  created_at: string;
}

interface Props {
  seedEmails: SeedEmail[];
  authUrl: string;
  errors?: any;
}

export default function Index({ seedEmails, authUrl, errors }: Props) {
  const [showAddForm, setShowAddForm] = useState(false);

  const { data, setData, post, processing, reset } = useForm({
    email: "",
    status: "active",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post("/admin/seed_emails", {
      onSuccess: () => {
        reset();
        setShowAddForm(false);
      },
    });
  };

  const updateStatus = (seedEmail: SeedEmail, newStatus: string) => {
    router.patch(`/admin/seed_emails/${seedEmail.id}`, {
      status: newStatus,
    });
  };

  const deleteSeedEmail = (seedEmail: SeedEmail) => {
    if (
      confirm(
        `Are you sure you want to delete the seed email "${seedEmail.email}"?`
      )
    ) {
      router.delete(`/admin/seed_emails/${seedEmail.id}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      case "unauthorized":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };


  return (
    <>
      <Head title="Admin - Seed Emails" />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Seed Email Management
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Manage Gmail accounts used for email tracking
                  </p>
                </div>
                <div className="flex space-x-4">
                  <a
                    href={authUrl}
                    className="bg-gradient-to-b from-green-600 to-green-700 text-white px-4 py-2 text-sm rounded-md"
                  >
                    Authorize Gmail Account
                  </a>
                  <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="bg-gradient-to-b from-blue-500 to-blue-600 text-white px-4 py-2 rounded-md text-sm cursor-pointer "
                  >
                    {showAddForm ? "Cancel" : "Add Seed Email"}
                  </button>
                  {/* <a
                    href="/dashboard"
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md"
                  >
                    Back to Dashboard
                  </a> */}
                </div>
              </div>

              {showAddForm && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={data.email}
                        onChange={(e) => setData("email", e.target.value)}
                        placeholder="seed@example.com"
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        required
                      />
                      {errors?.email && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.email[0]}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="status"
                        className="block text-sm font-medium text-gray-700"
                      >
                        Status
                      </label>
                      <Select value={data.status} onValueChange={(value) => setData("status", value)}>
                        <SelectTrigger className="mt-1 w-full">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        type="submit"
                        disabled={processing}
                        className="bg-gradient-to-b from-blue-500 to-blue-600 text-sm disabled:opacity-50 cursor-pointer text-white px-4 py-2 rounded-md"
                      >
                        {processing ? "Adding..." : "Add Seed Email"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddForm(false);
                          reset();
                        }}
                        className="bg-gray-300  text-sm cursor-pointer text-gray-700 px-4 py-2 rounded-md"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            <div className="p-6">
              {seedEmails.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No seed emails configured
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Add seed email accounts to start tracking email
                    deliverability. You'll need to authorize each Gmail account.
                  </p>
                  {/* <div className="space-x-4">
                    <button
                      onClick={() => setShowAddForm(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                    >
                      Add Seed Email
                    </button>
                    <a
                      href={authUrl}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md inline-block"
                    >
                      Authorize Gmail Account
                    </a>
                  </div> */}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Authorization
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Added
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {seedEmails.map((seedEmail) => (
                        <tr
                          key={seedEmail.id}
                          className="hover:bg-gray-50/50 transition-colors duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {seedEmail.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-md ${
                                !seedEmail.access_token
                                  ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20"
                                  : seedEmail.status === "active"
                                  ? "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20"
                                  : "bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10"
                              }`}
                            >
                              {!seedEmail.access_token
                                ? "Unauthorized"
                                : seedEmail.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {seedEmail.access_token ? (
                              <div>
                                <div className="inline-flex items-center px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded-md ring-1 ring-inset ring-green-600/20">
                                  Authorized
                                </div>
                                {seedEmail.token_expires_at && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Expires:{" "}
                                    {formatDateEST(seedEmail.token_expires_at)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="inline-flex items-center px-3 py-1 text-xs font-medium text-red-700 bg-red-50 rounded-md ring-1 ring-inset ring-red-600/20">
                                Not Authorized
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDateEST(seedEmail.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Select 
                                value={seedEmail.status} 
                                onValueChange={(value) => updateStatus(seedEmail, value)}
                              >
                                <SelectTrigger className="w-[100px] h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active">Active</SelectItem>
                                  <SelectItem value="inactive">Inactive</SelectItem>
                                </SelectContent>
                              </Select>
                              {!seedEmail.access_token && (
                                <a
                                  href={authUrl}
                                  className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                                >
                                  Authorize
                                </a>
                              )}
                              <button
                                onClick={() => deleteSeedEmail(seedEmail)}
                                className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors cursor-pointer"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
